// components/StudentRecord/ViewStudentRecordForm.jsx
import { motion, AnimatePresence } from "framer-motion";
import { STUDENT_SECTION_KEYS, STUDENT_SECTION_LABELS } from "../../../constants.js";
import StudentRecordForm from "./StudentRecordForm.js";
import ViewEnrolmentForm from "../EnrolmentForms/ViewEnrolmentForm.jsx";
import { SectionFeesPlan } from "./sections/SectionFeesPlan.jsx";
import { getEnrollment } from "../../../services/EnrolmentService.js";
import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, Edit2 } from "lucide-react";
import { SectionFollowUps } from "./sections/SectionFollowups.jsx";
import {SectionAgentCommission} from "./sections/SectionAgentCommission.jsx";
import {SectionAction} from "./sections/SectionActions.jsx";
import {FormField, Input, Button, Modal} from "../../SharedInputs.jsx";

export default function ViewStudentRecordForm({ onCancel, initialData }) {
  const [currentSection, setCurrentSection] = useState("studentRecord");

  const sectionMap = {
    studentRecord: StudentRecordForm,
    enrolmentForm: ViewEnrolmentForm,
    followUps: SectionFollowUps,
    feesPlan: SectionFeesPlan,
    agentCommissions: SectionAgentCommission,
    actions: SectionAction,
  };
  const coursesList = initialData?.formState?.courses || [];
  const [editingPackageIndex, setEditingPackageIndex] = useState(null);

  // const CurrentComponent = sectionMap[currentSection];
  const isCourseSection = currentSection.startsWith("feesPlan_");
  const selectedCourseCode = isCourseSection ? currentSection.replace("feesPlan_", "") : null;
  const CurrentComponent = isCourseSection ? SectionFeesPlan : sectionMap[currentSection];
  const [selectedCourse, setSelectedCourse] = useState(coursesList.find(c => c.courseName === selectedCourseCode));
  const [enrolmentData, setEnrolmentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feePlanExpanded, setFeePlanExpanded] = useState(true); 
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [newPackageName, setNewPackageName] = useState("");
  const [selectedCourseNames, setSelectedCourseNames] = useState([]);

  // Persistent Student Record reference shown while switching sections.
  const studentFirstName = initialData?.formState?.firstName || "";
  const studentLastName = initialData?.formState?.lastName || "";
  const studentName = `${studentFirstName} ${studentLastName}`.trim();
  const studentId = initialData?.formState?.studentId || "";

  useEffect(() => {
    const fetchEnrolment = async () => {
      if (initialData?.enrolmentReferenceId) {
        setLoading(true);
        try {
          const data = await getEnrollment(initialData.enrolmentReferenceId);
          setEnrolmentData(data);
        } catch (error) {
          console.error("Failed to fetch enrolment data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEnrolment();
  }, [initialData]);
  // Save package
  const handleSavePackage = () => {
    if (!newPackageName) return alert("Enter package name.");

    // Build selected courses object
    const packageCourses = {};
    selectedCourseNames.forEach((name) => {
      const c = coursesList.find((course) => course.courseName === name);
      if (c) packageCourses[name] = { ...c };
    });

    if (!initialData.formState.packages) initialData.formState.packages = [];

    if (editingPackageIndex !== null) {
      // Update existing package: keep existing feesPlan
      const existingPackage = initialData.formState.packages[editingPackageIndex];
      const updatedPackage = {
        ...existingPackage, // preserve feesPlan
        name: newPackageName,
        courses: packageCourses,
      };
      initialData.formState.packages[editingPackageIndex] = updatedPackage;
    } else {
      // Add new package
      const newPackage = {
        name: newPackageName,
        courses: packageCourses,
        feesPlan: {}, // empty for new package
      };
      initialData.formState.packages.push(newPackage);
      setSelectedCourse(newPackage);
    }

    setCurrentSection(`feesPlan_package_${newPackageName}`);

    // Reset modal state
    setNewPackageName("");
    setSelectedCourseNames([]);
    setEditingPackageIndex(null);
    setShowPackageModal(false);
  };



  return (
    <div style={{ display: "flex", height: "100%", background: "#fff" }}>
      {/* Sidebar */}
      <div
        style={{
          width: 240,
          background: "#f9f9f9",
          borderRight: "2px solid #ddd",
          padding: "16px 0",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Standard Sections */}
        {STUDENT_SECTION_KEYS.filter(k => k !== "feesPlan").map((key) => (
          <div
            key={key}
            onClick={() => setCurrentSection(key)}
            style={{
              padding: "12px 20px",
              cursor: "pointer",
              color: currentSection === key ? "#007bff" : "#333",
              fontWeight: currentSection === key ? "bold" : "normal",
              backgroundColor: currentSection === key ? "#e6f0ff" : "transparent",
              borderLeft: currentSection === key ? "4px solid #007bff" : "4px solid transparent",
              marginBottom: 4,
              transition: "0.2s all",
            }}
          >
            {STUDENT_SECTION_LABELS[key]}
          </div>
        ))}

        {/* Fee Plan Parent */}
        <div
          onClick={() => setFeePlanExpanded(!feePlanExpanded)}
          style={{
            padding: "12px 20px",
            cursor: "pointer",
            color: currentSection.startsWith("feesPlan_") ? "#007bff" : "#333",
            fontWeight: currentSection.startsWith("feesPlan_") ? "bold" : "",
            backgroundColor: currentSection.startsWith("feesPlan_") ? "#e6f0ff" : "transparent",
            borderLeft: currentSection.startsWith("feesPlan_") ? "4px solid #007bff" : "4px solid transparent",
            marginBottom: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            transition: "0.2s all",
          }}
        >
          <span>Fee Plan</span>
          {feePlanExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        {/* Fee Plan Sub-items */}
        {feePlanExpanded && (
          <>
            {initialData?.formState?.feePlanType === "Package" ? (
              <>
                {/* Existing Packages */}
                {(initialData.formState.packages || []).map((pkg, idx) => (
                <div
                  key={pkg.name || `package-${idx}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 40px",
                    cursor: "pointer",
                    color: currentSection === `feesPlan_package_${pkg.name}` ? "#007bff" : "#333",
                    fontWeight: currentSection === `feesPlan_package_${pkg.name}` ? "bold" : "normal",
                    backgroundColor:
                      currentSection === `feesPlan_package_${pkg.name}` ? "#e6f0ff" : "transparent",
                    borderLeft:
                      currentSection === `feesPlan_package_${pkg.name}`
                        ? "4px solid #007bff"
                        : "4px solid transparent",
                    marginBottom: 4,
                    transition: "0.2s all",
                  }}
                >
                  <span
                    onClick={() => {
                      setCurrentSection(`feesPlan_package_${pkg.name}`);
                      setSelectedCourse(pkg);
                    }}
                  >
                    {pkg.name}
                  </span>

                  {/* Edit icon */}
                  <Edit2
                    size={16}
                    color="#007bff"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent selecting the package
                      setNewPackageName(pkg.name);
                      setSelectedCourseNames(Object.keys(pkg.courses || {}));
                      setEditingPackageIndex(idx);
                      setShowPackageModal(true);
                    }}
                  />
                </div>
              ))}

              
                {/* Add New Package */}
                <div
                  onClick={() => setShowPackageModal(true)}
                  style={{
                    padding: "12px 40px",
                    cursor: "pointer",
                    color: "#007bff",
                    fontWeight: 500,
                    marginTop: 8,
                  }}
                >
                  + Add Package
                </div>
              </>
            ) : (
              // 🔹 Default per-course fee plan list
              coursesList.map((course) => (
                <div
                  key={course.courseName}
                  onClick={() => {
                    setCurrentSection(`feesPlan_${course.courseName}`);
                    setSelectedCourse(coursesList.find((c) => c.courseName === course.courseName));
                  }}
                  style={{
                    padding: "12px 40px",
                    cursor: "pointer",
                    color: currentSection === `feesPlan_${course.courseName}` ? "#007bff" : "#333",
                    fontWeight: currentSection === `feesPlan_${course.courseName}` ? "bold" : "normal",
                    backgroundColor:
                      currentSection === `feesPlan_${course.courseName}` ? "#e6f0ff" : "transparent",
                    borderLeft:
                      currentSection === `feesPlan_${course.courseName}`
                        ? "4px solid #007bff"
                        : "4px solid transparent",
                    marginBottom: 4,
                    transition: "0.2s all",
                  }}
                >
                  {course.courseName}
                </div>
              ))
            )}
          </>
        )}
    


        {["agentCommissions", "followUps", "actions"].filter(k => k !== "feesPlan").map((key) => (
          <div
            key={key}
            onClick={() => setCurrentSection(key)}
            style={{
              padding: "12px 20px",
              cursor: "pointer",
              color: currentSection === key ? "#007bff" : "#333",
              fontWeight: currentSection === key ? "bold" : "normal",
              backgroundColor: currentSection === key ? "#e6f0ff" : "transparent",
              borderLeft: currentSection === key ? "4px solid #007bff" : "4px solid transparent",
              marginBottom: 4,
              transition: "0.2s all",
            }}
          >
            {STUDENT_SECTION_LABELS[key]}
          </div>
        ))}


      </div>

      {/* Content Area */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: "#fff",
        }}
      >
        {/* Persistent Student Record Header */}
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid #ddd",
            background: "#f9f9f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "0.78rem",
                color: "#666",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 2,
              }}
            >
              Student Record
            </div>
            <div
              style={{
                fontSize: "1.15rem",
                fontWeight: 700,
                color: "#222",
              }}
            >
              {studentName || "Student"}
            </div>
          </div>

          {studentId && (
            <div
              style={{
                fontSize: "0.9rem",
                color: "#555",
                whiteSpace: "nowrap",
              }}
            >
              <strong>Student ID:</strong> {studentId}
            </div>
          )}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            padding: currentSection !== "enrolmentForm" ? 20 : 0,
          }}
        >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSection}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            {initialData ? (
              initialData.enrolmentReferenceId !== "" && currentSection === "enrolmentForm" ? (
                <CurrentComponent onCancel={onCancel} initialData={enrolmentData} />
              ) : currentSection === "followUps" ? (
                <CurrentComponent onCancel={onCancel} initialData={initialData} />
              ) : currentSection === "agentCommissions" ? (
                <CurrentComponent onCancel={onCancel} initialData={initialData} />
              ) :  currentSection.startsWith("studentRecord") ? (
                <CurrentComponent onCancel={onCancel} initialData={initialData}/>
              ) : currentSection.startsWith("actions") ? (
                // Offer Letter / Actions use Student Record data directly.
                <CurrentComponent
                  onCancel={onCancel}
                  initialData={initialData}
                />
              ) : currentSection.startsWith("feesPlan_") ? (
                <CurrentComponent
                  onCancel={onCancel}
                  initialData={initialData}
                  course={selectedCourse}
                  isCustomPackage={currentSection.includes("package_")}
                />
              )
              : (
                <div style={{ padding: "20px", color: "#666" }}>
                  <h2>No Enrolment Data</h2>
                  <p>This student is not linked with any enrolment data.</p>
                </div>
              )
            ) : (
              <div style={{ padding: "20px", color: "#666" }}>
                <h2>No Enrolment Data</h2>
                <p>This student is not linked with any enrolment data.</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        </div>
      </div>
      {/* Modal */}
      {showPackageModal && (
        <Modal
          title={editingPackageIndex !== null ? "Edit Package" : "Create New Package"}
          onClose={() => setShowPackageModal(false)}
        >
          <FormField label="Package Name">
            <Input
              type="text"
              placeholder="Enter package name"
              value={newPackageName}
              onChange={(e) => setNewPackageName(e.target.value)}
            />
          </FormField>

          <FormField label="Select Courses">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {coursesList.map((c) => (
                <label key={c.courseName} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={selectedCourseNames.includes(c.courseName)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedCourseNames([...selectedCourseNames, c.courseName]);
                      } else {
                        setSelectedCourseNames(
                          selectedCourseNames.filter((name) => name !== c.courseName)
                        );
                      }
                    }}
                  />
                  {c.courseName}
                </label>
              ))}
            </div>
          </FormField>

          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="secondary" onClick={() => setShowPackageModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePackage}>
              {editingPackageIndex !== null ? "Update Package" : "Save Package"}
            </Button>
          </div>
        </Modal>
      )}

    </div>
  );
}